import React, {
    useEffect,
    useRef,
    useState
} from 'react';

import './Outils.css';
import './Outils.mobile.css';


/* ============================================================
   DATA
   ============================================================ */

const PROGRAMMING_LANGUAGES = [
    {
        language_name_display: 'C / C++',
        skill_level_percentage: 85,
        technical_details_array: [
            'Gestion mémoire',
            'Multithreading',
            'POO',
            'Algorithmie'
        ]
    },
    {
        language_name_display: 'Python',
        skill_level_percentage: 90,
        technical_details_array: [
            'Automatisation',
            'Scripting',
            'Scraping',
            'PyQt'
        ]
    },
    {
        language_name_display: 'JavaScript',
        skill_level_percentage: 80,
        technical_details_array: [
            'Manipulation DOM',
            'POO'
        ]
    },
    {
        language_name_display: 'Bash',
        skill_level_percentage: 75,
        technical_details_array: [
            'Terminal Linux',
            'Automation',
            'Scripting'
        ]
    }
];


const DEVELOPMENT_TOOLS = [
    {
        tool_name_display: 'Git / GitHub',
        skill_level_percentage: 90
    },
    {
        tool_name_display: 'Docker',
        skill_level_percentage: 75
    },
    {
        tool_name_display: 'Postman',
        skill_level_percentage: 85
    },
    {
        tool_name_display: 'Linux',
        skill_level_percentage: 90
    }
];


const ADDITIONAL_TECHNICAL_CAPACITIES = [
    {
        capacity_category_title: 'APIs & MÉDIAS',
        category_items_array: [
            'FFMPEG',
            'OpenAI API',
            'Claude API',
            'Telegram API'
        ]
    },
    {
        capacity_category_title: 'MARKETING TECH',
        category_items_array: [
            'Google Ads'
        ]
    }
];


const OPERATIONAL_STRATEGIES = [
    {
        index: '01',
        title: 'ARCHITECTURE',
        description: 'Clean Code & Microservices'
    },
    {
        index: '02',
        title: 'DEVOPS',
        description: 'Pipelines CI/CD & Cloud'
    },
    {
        index: '03',
        title: 'PRODUIT',
        description: 'Prompt Engineering & Agile'
    },
    {
        index: '04',
        title: 'AUTOMATISATION',
        description: 'Workflows & Scripting'
    }
];


const AI_INFRASTRUCTURE = [
    {
        ai_capability_name: 'Orchestration LLM',
        technology_stack_description: 'LangChain, CrewAI',
        use_case_description: 'Systèmes multi-agents & RAG'
    },
    {
        ai_capability_name: 'Vision & Signal',
        technology_stack_description: 'OpenCV, Whisper',
        use_case_description: 'Image, Vidéo & Audio'
    },
    {
        ai_capability_name: 'Bases Vectorielles',
        technology_stack_description: 'Pinecone, ChromaDB',
        use_case_description: 'Mémoire haute performance'
    },
    {
        ai_capability_name: 'Fine-tuning',
        technology_stack_description: 'LoRA, QLoRA',
        use_case_description: 'Optimisation Mistral / Llama'
    }
];


/* ============================================================
   REUSABLE COMPONENTS
   ============================================================ */

const ProgressBar = ({
    percentage,
    mounted,
    inverted = false
}) => {
    return (
        <div
            className={`
                outils-skill-progress-bar-track-container
                ${
                    inverted
                        ? 'outils-skill-progress-bar-track-inverted-dark'
                        : ''
                }
            `}
        >
            <div
                className="outils-skill-progress-bar-fill-animation-red"
                style={{
                    width: mounted
                        ? `${percentage}%`
                        : '0%'
                }}
            />
        </div>
    );
};


const TechnologyTags = ({
    items,
    inverted = false
}) => {
    return (
        <div
            className={`
                outils-technology-details-tag-container-flex
                ${
                    inverted
                        ? 'outils-technology-tags-inverted'
                        : ''
                }
            `}
        >
            {items.map((item, index) => (
                <React.Fragment key={item}>

                    <span className="outils-technology-detail-tag-text-uppercase">
                        {item}
                    </span>

                    {index < items.length - 1 && (
                        <span className="outils-technology-separator-red-pipe-symbol">
                            |
                        </span>
                    )}

                </React.Fragment>
            ))}
        </div>
    );
};


/* ============================================================
   MAIN COMPONENT
   ============================================================ */

const Outils = () => {

    const [
        component_is_mounted_and_ready_for_animations,
        set_component_mounted_state
    ] = useState(false);


    const [
        activeMobileSlide,
        setActiveMobileSlide
    ] = useState(0);


    const mobileCarouselRef = useRef(null);

    const currentYear =
        new Date().getFullYear();


    useEffect(() => {
        set_component_mounted_state(true);
    }, []);


    /* ============================================================
       MOBILE CAROUSEL
       ============================================================ */

    const handleMobileCarouselScroll = () => {

        const carousel =
            mobileCarouselRef.current;

        if (!carousel) {
            return;
        }


        const slides =
            Array.from(
                carousel.querySelectorAll(
                    '.outils-mobile-carousel-slide'
                )
            );


        if (!slides.length) {
            return;
        }


        const carouselRect =
            carousel.getBoundingClientRect();


        const carouselCenter =
            carouselRect.left +
            carouselRect.width / 2;


        let closestIndex = 0;
        let closestDistance = Infinity;


        slides.forEach((slide, index) => {

            const slideRect =
                slide.getBoundingClientRect();


            const slideCenter =
                slideRect.left +
                slideRect.width / 2;


            const distance =
                Math.abs(
                    slideCenter -
                    carouselCenter
                );


            if (distance < closestDistance) {

                closestDistance =
                    distance;

                closestIndex =
                    index;
            }

        });


        setActiveMobileSlide(
            closestIndex
        );
    };


    return (

        <section className="outils-dashboard-main-viewport-wrapper">


            {/* ====================================================
                HEADER
                ==================================================== */}

            <header className="outils-dashboard-header-container-full">

                <div className="outils-header-branding-title-group">

                    <h1 className="outils-header-outils-main-title-large-display">
                        EXPERTISE TECHNIQUE
                    </h1>


                    <p className="outils-header-subtitle-technical-description">
                        // STACK_{currentYear} // CONTRÔLE_TOTAL
                    </p>

                </div>

            </header>


            {/* ====================================================
                DESKTOP
                ==================================================== */}

            <main className="outils-dashboard-content-grid-twelve-columns outils-desktop-content">


                {/* =================================================
                    01 — LANGAGES
                    ================================================= */}

                <section className="outils-grid-column-languages-section-three-units">

                    <div className="outils-card-brutal-design-white-background">


                        <div className="outils-card-header-section-with-number-and-title">

                            <span className="outils-card-header-number-badge-black-background">
                                01
                            </span>


                            <div className="outils-card-header-title-text-large-display">
                                <h2>
                                    LANGAGES
                                </h2>
                            </div>

                        </div>


                        <div className="outils-card-body-content-scrollable-area">

                            {PROGRAMMING_LANGUAGES.map(
                                (language) => (

                                    <div
                                        key={
                                            language.language_name_display
                                        }
                                        className="outils-skill-item-container-with-progress-bar"
                                    >

                                        <div className="outils-skill-info-header-name-and-percentage">

                                            <span>
                                                {
                                                    language.language_name_display
                                                }
                                            </span>


                                            <span>
                                                {
                                                    language.skill_level_percentage
                                                }%
                                            </span>

                                        </div>


                                        <ProgressBar
                                            percentage={
                                                language.skill_level_percentage
                                            }
                                            mounted={
                                                component_is_mounted_and_ready_for_animations
                                            }
                                        />


                                        <TechnologyTags
                                            items={
                                                language.technical_details_array
                                            }
                                        />

                                    </div>

                                )
                            )}

                        </div>

                    </div>

                </section>


                {/* =================================================
                    02 — STRUCTURE & OUTILS
                    ================================================= */}

                <section className="outils-grid-column-tools-framework-section-five-units">

                    <div className="outils-card-brutal-design-white-background outils-card-brutal-design-black-background-inverted">


                        <div className="outils-card-header-section-with-number-and-title">

                            <span className="outils-card-header-number-badge-white-background-inverted">
                                02
                            </span>


                            <div className="outils-card-header-title-text-large-display">
                                <h2>
                                    STRUCTURE & OUTILS
                                </h2>
                            </div>

                        </div>


                        <div className="outils-card-body-content-scrollable-area">


                            <div className="outils-framework-showcase-grid-two-columns">


                                <div className="outils-framework-mini-module-box-ivory-background">

                                    <div className="outils-framework-mini-module-title-large">
                                        <h3>
                                            React
                                        </h3>
                                    </div>


                                    <div className="outils-framework-mini-module-description-text">
                                        <p>
                                            Frontend / Hooks
                                        </p>
                                    </div>

                                </div>


                                <div className="outils-framework-mini-module-box-ivory-background">

                                    <div className="outils-framework-mini-module-title-large">
                                        <h3>
                                            PyQt
                                        </h3>
                                    </div>


                                    <div className="outils-framework-mini-module-description-text">
                                        <p>
                                            GUI Desktop
                                        </p>
                                    </div>

                                </div>

                            </div>


                            <div className="outils-tools-control-center-section-container">


                                <h3 className="outils-tools-section-inner-title-uppercase">
                                    // CENTRE_DE_CONTRÔLE
                                </h3>


                                {DEVELOPMENT_TOOLS.map(
                                    (tool) => (

                                        <div
                                            key={
                                                tool.tool_name_display
                                            }
                                            className="outils-skill-item-container-with-progress-bar"
                                        >

                                            <div className="outils-skill-info-header-name-and-percentage outils-skill-info-header-inverted-white-text">

                                                <span>
                                                    {
                                                        tool.tool_name_display
                                                    }
                                                </span>


                                                <span>
                                                    {
                                                        tool.skill_level_percentage
                                                    }%
                                                </span>

                                            </div>


                                            <ProgressBar
                                                percentage={
                                                    tool.skill_level_percentage
                                                }
                                                mounted={
                                                    component_is_mounted_and_ready_for_animations
                                                }
                                                inverted
                                            />

                                        </div>

                                    )
                                )}


                                <div className="outils-extra-capacities-api-section-dashed-border">

                                    {ADDITIONAL_TECHNICAL_CAPACITIES.map(
                                        (capacity) => (

                                            <div
                                                key={
                                                    capacity.capacity_category_title
                                                }
                                                className="outils-extra-capacity-detail-box-container"
                                            >

                                                <div className="outils-extra-capacity-category-title-red-bold">

                                                    <strong>
                                                        {
                                                            capacity.capacity_category_title
                                                        }
                                                    </strong>

                                                </div>


                                                <TechnologyTags
                                                    items={
                                                        capacity.category_items_array
                                                    }
                                                    inverted
                                                />

                                            </div>

                                        )
                                    )}

                                </div>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =================================================
                    03 — OPÉRATIONNEL
                    ================================================= */}

                <section className="outils-grid-column-operational-strategy-section-four-units">

                    <div className="outils-card-brutal-design-white-background">


                        <div className="outils-card-header-section-with-number-and-title">

                            <span className="outils-card-header-number-badge-black-background">
                                03
                            </span>


                            <div className="outils-card-header-title-text-large-display">
                                <h2>
                                    OPÉRATIONNEL
                                </h2>
                            </div>

                        </div>


                        <div className="outils-card-body-content-scrollable-area">


                            <div className="outils-operational-strategy-list-vertical-layout">

                                {OPERATIONAL_STRATEGIES.map(
                                    (strategy) => (

                                        <div
                                            key={
                                                strategy.title
                                            }
                                            className="outils-strategy-item-container-red-left-border"
                                        >

                                            <div className="outils-strategy-item-title-text-bold">

                                                <strong>
                                                    {
                                                        strategy.title
                                                    }
                                                </strong>

                                            </div>


                                            <div className="outils-strategy-item-description-text-paragraph">

                                                <p>
                                                    {
                                                        strategy.description
                                                    }
                                                </p>

                                            </div>

                                        </div>

                                    )
                                )}

                            </div>

                        </div>

                    </div>

                </section>


                {/* =================================================
                    04 — INFRASTRUCTURE IA
                    ================================================= */}

                <section className="outils-grid-column-ai-infrastructure-section-twelve-units">

                    <div className="outils-card-brutal-design-white-background outils-card-brutal-design-ai-themed-dark-with-red-accent">


                        <div className="outils-card-header-section-with-number-and-title">

                            <span className="outils-card-header-number-badge-red-background-accent">
                                04
                            </span>


                            <div className="outils-card-header-title-text-large-display">
                                <h2>
                                    INFRASTRUCTURE IA
                                </h2>
                            </div>


                            <span className="outils-neural-connection-status-blinking-tag">
                                LIAISON_NEURALE: OK
                            </span>

                        </div>


                        <div className="outils-card-body-content-scrollable-area">

                            <div className="outils-ai-infrastructure-grid-four-columns">

                                {AI_INFRASTRUCTURE.map(
                                    (capability) => (

                                        <div
                                            key={
                                                capability.ai_capability_name
                                            }
                                            className="outils-ai-capability-sub-box-white-background"
                                        >

                                            <h4 className="outils-ai-capability-title-text-red-bold">
                                                {
                                                    capability.ai_capability_name
                                                }
                                            </h4>


                                            <div className="outils-ai-capability-stack-label-black-bold">
                                                {
                                                    capability.technology_stack_description
                                                }
                                            </div>


                                            <div className="outils-ai-capability-description-paragraph-text">

                                                <p>
                                                    {
                                                        capability.use_case_description
                                                    }
                                                </p>

                                            </div>

                                        </div>

                                    )
                                )}

                            </div>

                        </div>

                    </div>

                </section>

            </main>


            {/* ====================================================
                MOBILE CAROUSEL
                ==================================================== */}

            <div className="outils-mobile-carousel-shell">


                <div
                    ref={mobileCarouselRef}
                    className="outils-mobile-carousel-track"
                    onScroll={
                        handleMobileCarouselScroll
                    }
                    role="region"
                    aria-roledescription="carrousel"
                    aria-label="Expertise technique"
                >


                    {/* ============================================
                        01 — LANGAGES
                        ============================================ */}

                    <article
                        className={`
                            outils-mobile-carousel-slide
                            outils-mobile-slide-light
                            outils-mobile-slide-languages
                            ${
                                activeMobileSlide === 0
                                    ? 'is-active'
                                    : ''
                            }
                        `}
                    >


                        <header className="outils-mobile-slide-header">

                            <span className="outils-mobile-slide-number">
                                01
                            </span>


                            <div className="outils-mobile-slide-heading">

                                <span className="outils-mobile-slide-kicker">
                                    STACK
                                </span>


                                <h2>
                                    LANGAGES
                                </h2>

                            </div>

                        </header>


                        <div className="outils-mobile-language-performance-list">

                            {PROGRAMMING_LANGUAGES.map(
                                (
                                    language,
                                    index
                                ) => (

                                    <div
                                        key={
                                            language.language_name_display
                                        }
                                        className="outils-mobile-language-performance-row"
                                    >

                                        <span className="outils-mobile-language-index">
                                            {
                                                String(
                                                    index + 1
                                                ).padStart(
                                                    2,
                                                    '0'
                                                )
                                            }
                                        </span>


                                        <div className="outils-mobile-language-performance-content">


                                            <div className="outils-mobile-language-main-line">

                                                <strong className="outils-mobile-language-name">
                                                    {
                                                        language.language_name_display
                                                    }
                                                </strong>


                                                <span className="outils-mobile-language-percentage">

                                                    {
                                                        language.skill_level_percentage
                                                    }

                                                    <small>
                                                        %
                                                    </small>

                                                </span>

                                            </div>


                                            <div className="outils-mobile-language-progress-track">

                                                <span
                                                    className="outils-mobile-language-progress-fill"
                                                    style={{
                                                        width:
                                                            component_is_mounted_and_ready_for_animations
                                                                ? `${language.skill_level_percentage}%`
                                                                : '0%'
                                                    }}
                                                />

                                            </div>


                                            <div className="outils-mobile-language-details">

                                                {
                                                    language.technical_details_array.map(
                                                        (
                                                            detail,
                                                            detailIndex
                                                        ) => (

                                                            <React.Fragment key={detail}>

                                                                <span>
                                                                    {
                                                                        detail
                                                                    }
                                                                </span>


                                                                {
                                                                    detailIndex <
                                                                    language.technical_details_array.length - 1 && (

                                                                        <i aria-hidden="true">
                                                                            /
                                                                        </i>

                                                                    )
                                                                }

                                                            </React.Fragment>

                                                        )
                                                    )
                                                }

                                            </div>

                                        </div>

                                    </div>

                                )
                            )}

                        </div>


                        <footer className="outils-mobile-language-footer">

                            <span>
                                CORE_STACK
                            </span>

                            <strong>
                                04 LANGAGES
                            </strong>

                        </footer>

                    </article>


                    {/* ============================================
                        02 — STRUCTURE & OUTILS
                        ============================================ */}

                    <article
                        className={`
                            outils-mobile-carousel-slide
                            outils-mobile-slide-dark
                            outils-mobile-slide-control-center
                            ${
                                activeMobileSlide === 1
                                    ? 'is-active'
                                    : ''
                            }
                        `}
                    >


                        <header className="outils-mobile-slide-header">

                            <span className="outils-mobile-slide-number">
                                02
                            </span>


                            <div className="outils-mobile-slide-heading">

                                <span className="outils-mobile-slide-kicker">
                                    CONTROL_CENTER
                                </span>


                                <h2>
                                    STRUCTURE & OUTILS
                                </h2>

                            </div>

                        </header>


                        <div className="outils-mobile-framework-feature-row">


                            <div className="outils-mobile-framework-feature">

                                <span>
                                    01
                                </span>

                                <strong>
                                    React
                                </strong>

                                <small>
                                    Frontend / Hooks
                                </small>

                            </div>


                            <div className="outils-mobile-framework-feature">

                                <span>
                                    02
                                </span>

                                <strong>
                                    PyQt
                                </strong>

                                <small>
                                    GUI Desktop
                                </small>

                            </div>

                        </div>


                        <div className="outils-mobile-control-center-label">
                            // CENTRE_DE_CONTRÔLE
                        </div>


                        <div className="outils-mobile-control-tool-list">

                            {DEVELOPMENT_TOOLS.map(
                                (tool) => (

                                    <div
                                        key={
                                            tool.tool_name_display
                                        }
                                        className="outils-mobile-control-tool-row"
                                    >


                                        <div className="outils-mobile-control-tool-info">

                                            <strong>
                                                {
                                                    tool.tool_name_display
                                                }
                                            </strong>


                                            <span>
                                                {
                                                    tool.skill_level_percentage
                                                }%
                                            </span>

                                        </div>


                                        <div className="outils-mobile-control-progress">

                                            <span
                                                style={{
                                                    width:
                                                        component_is_mounted_and_ready_for_animations
                                                            ? `${tool.skill_level_percentage}%`
                                                            : '0%'
                                                }}
                                            />

                                        </div>

                                    </div>

                                )
                            )}

                        </div>


                        <div className="outils-mobile-control-footer">

                            {ADDITIONAL_TECHNICAL_CAPACITIES.map(
                                (capacity) => (

                                    <div
                                        key={
                                            capacity.capacity_category_title
                                        }
                                    >

                                        <strong>
                                            {
                                                capacity.capacity_category_title
                                            }
                                        </strong>


                                        <span>
                                            {
                                                capacity.category_items_array.join(
                                                    ' / '
                                                )
                                            }
                                        </span>

                                    </div>

                                )
                            )}

                        </div>

                    </article>


                    {/* ============================================
                        03 — OPÉRATIONNEL
                        ============================================ */}

                    <article
                        className={`
                            outils-mobile-carousel-slide
                            outils-mobile-slide-light
                            outils-mobile-slide-operational
                            ${
                                activeMobileSlide === 2
                                    ? 'is-active'
                                    : ''
                            }
                        `}
                    >


                        <header className="outils-mobile-slide-header">

                            <span className="outils-mobile-slide-number">
                                03
                            </span>


                            <div className="outils-mobile-slide-heading">

                                <span className="outils-mobile-slide-kicker">
                                    EXECUTION
                                </span>


                                <h2>
                                    OPÉRATIONNEL
                                </h2>

                            </div>

                        </header>


                        <div className="outils-mobile-operational-list">

                            {OPERATIONAL_STRATEGIES.map(
                                (strategy) => (

                                    <div
                                        key={
                                            strategy.title
                                        }
                                        className="outils-mobile-operational-row"
                                    >


                                        <span className="outils-mobile-operational-index">
                                            {
                                                strategy.index
                                            }
                                        </span>


                                        <div className="outils-mobile-operational-content">

                                            <strong>
                                                {
                                                    strategy.title
                                                }
                                            </strong>


                                            <p>
                                                {
                                                    strategy.description
                                                }
                                            </p>

                                        </div>


                                        <span
                                            className="outils-mobile-operational-marker"
                                            aria-hidden="true"
                                        >
                                            →
                                        </span>

                                    </div>

                                )
                            )}

                        </div>


                        <div className="outils-mobile-operational-footer">

                            <span>
                                METHOD
                            </span>


                            <strong>
                                DESIGN → BUILD → SHIP
                            </strong>

                        </div>

                    </article>


                    {/* ============================================
                        04 — INFRASTRUCTURE IA
                        ============================================ */}

                    <article
                        className={`
                            outils-mobile-carousel-slide
                            outils-mobile-slide-ai
                            ${
                                activeMobileSlide === 3
                                    ? 'is-active'
                                    : ''
                            }
                        `}
                    >


                        <header className="outils-mobile-slide-header">

                            <span className="outils-mobile-slide-number">
                                04
                            </span>


                            <div className="outils-mobile-slide-heading">

                                <span className="outils-mobile-slide-kicker">
                                    LIAISON_NEURALE: OK
                                </span>


                                <h2>
                                    INFRASTRUCTURE IA
                                </h2>

                            </div>

                        </header>


                        <div className="outils-mobile-ai-grid">

                            {AI_INFRASTRUCTURE.map(
                                (capability) => (

                                    <div
                                        key={
                                            capability.ai_capability_name
                                        }
                                        className="outils-mobile-ai-module"
                                    >

                                        <strong>
                                            {
                                                capability.ai_capability_name
                                            }
                                        </strong>


                                        <span>
                                            {
                                                capability.technology_stack_description
                                            }
                                        </span>


                                        <p>
                                            {
                                                capability.use_case_description
                                            }
                                        </p>

                                    </div>

                                )
                            )}

                        </div>

                    </article>

                </div>

            </div>

        </section>

    );
};


export default Outils;